import { useEffect } from 'react';
import { create } from 'zustand';
import { LiveMessage, type SiteLive } from '@ste/shared';
import { useAuth } from './auth.ts';
import { siteBase, useConnection } from './connection.ts';

type ConnectionStatus = 'connecting' | 'open' | 'closed';

interface LiveState {
  live: SiteLive | undefined;
  status: ConnectionStatus;
}

export const useLiveStore = create<LiveState>(() => ({ live: undefined, status: 'closed' }));

function websocketUrl(): string {
  const base = new URL(siteBase(), window.location.href);
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${base.toString().replace(/\/$/, '')}/live/ws`;
}

/**
 * Keeps one websocket to the gateway while mounted. Authenticates with the first
 * message, re-sends fresh tokens before expiry and reconnects with backoff.
 */
export function useLiveConnection(): void {
  const authenticated = useAuth((s) => s.status === 'authenticated');
  const mode = useConnection((s) => s.mode);
  const siteId = useConnection((s) => s.siteId);

  useEffect(() => {
    if (!authenticated || !mode || (mode === 'cloud' && !siteId)) return;
    useLiveStore.setState({ live: undefined });
    let socket: WebSocket | undefined;
    let stopped = false;
    let attempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let reauthTimer: ReturnType<typeof setInterval> | undefined;

    const sendAuth = async () => {
      const token = await useAuth.getState().getAccessToken();
      if (token && socket?.readyState === WebSocket.OPEN)
        socket.send(JSON.stringify({ type: 'auth', token }));
    };

    const connect = () => {
      useLiveStore.setState({ status: 'connecting' });
      const ws = new WebSocket(websocketUrl());
      socket = ws;
      ws.onopen = () => {
        void sendAuth();
        // access tokens live 15 min; re-authenticate well before
        reauthTimer = setInterval(() => void sendAuth(), 5 * 60_000);
      };
      ws.onmessage = (event) => {
        const parsed = LiveMessage.safeParse(JSON.parse(String(event.data)));
        if (!parsed.success) return;
        if (parsed.data.type === 'live') {
          // only real data resets the backoff: STE Connect accepts the socket even when the
          // site is unreachable, and would otherwise be asked again every half second
          attempt = 0;
          useLiveStore.setState({ live: parsed.data.data, status: 'open' });
        } else if (parsed.data.error === 'unauthorized') {
          useAuth.setState({ expiresAt: 0 });
        }
      };
      ws.onclose = () => {
        clearInterval(reauthTimer);
        if (stopped) return;
        useLiveStore.setState({ status: 'closed' });
        const delay = Math.min(15_000, 500 * 2 ** attempt++);
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      clearInterval(reauthTimer);
      socket?.close();
      useLiveStore.setState({ status: 'closed' });
    };
  }, [authenticated, mode, siteId]);
}

export function useLive(): SiteLive | undefined {
  return useLiveStore((s) => s.live);
}
